import { HttpTypes } from "@medusajs/types"

/**
 * Sort categories by Medusa `rank` (0 = first). Missing rank goes last; ties break by name (sl).
 */
export function compareCategoriesByRank(
  a: Pick<HttpTypes.StoreProductCategory, "rank" | "name">,
  b: Pick<HttpTypes.StoreProductCategory, "rank" | "name">
): number {
  const rankA = typeof a.rank === "number" ? a.rank : Number.POSITIVE_INFINITY
  const rankB = typeof b.rank === "number" ? b.rank : Number.POSITIVE_INFINITY
  if (rankA !== rankB) return rankA - rankB
  return (a.name ?? "").localeCompare(b.name ?? "", "sl")
}

/** @deprecated Use {@link compareCategoriesByRank}. */
export const compareCategoriesByDisplayOrder = compareCategoriesByRank

/**
 * Get all category IDs including subcategories recursively
 */
export const getAllCategoryIds = (
  categoryId: string,
  allCategories: HttpTypes.StoreProductCategory[]
): string[] => {
  const ids = [categoryId]
  const findSubcategoryIds = (parentId: string) => {
    const subcategories = allCategories.filter(
      (cat) => cat.parent_category_id === parentId
    )
    subcategories.forEach((subcat) => {
      ids.push(subcat.id)
      findSubcategoryIds(subcat.id)
    })
  }
  findSubcategoryIds(categoryId)
  return ids
}

/**
 * Get all category handles including subcategories recursively
 */
export const getAllCategoryHandles = (
  categoryHandle: string,
  categoryId: string,
  allCategories: HttpTypes.StoreProductCategory[]
): string[] => {
  const handles = [categoryHandle]
  
  const findSubcategoryHandles = (parentId: string) => {
    const subcategories = allCategories.filter(
      (cat) => cat.parent_category_id === parentId
    )
    subcategories.forEach((subcat) => {
      if (subcat.handle) {
        handles.push(subcat.handle)
      }
      findSubcategoryHandles(subcat.id) // Recursive for deeper levels
    })
  }
  
  findSubcategoryHandles(categoryId)
  return handles
}

/**
 * Map category ID to category name
 */
export const getCategoryNameById = (
  categoryId: string,
  categories: HttpTypes.StoreProductCategory[]
): string => {
  const category = categories.find(cat => cat.id === categoryId)
  return category?.name || categoryId
}

/**
 * Get hierarchical categories grouped by parent
 * Returns main categories with their subcategories
 */
export const getHierarchicalCategories = (
  categories: HttpTypes.StoreProductCategory[]
): {
  mainCategories: HttpTypes.StoreProductCategory[]
  subcategoriesByParent: Record<string, HttpTypes.StoreProductCategory[]>
} => {
  const mainCategories = categories
    .filter((cat) => cat.parent_category_id === null)
    .sort(compareCategoriesByRank)
  const subcategoriesByParent: Record<string, HttpTypes.StoreProductCategory[]> = {}

  mainCategories.forEach((mainCat) => {
    subcategoriesByParent[mainCat.id] = categories
      .filter((cat) => cat.parent_category_id === mainCat.id)
      .sort(compareCategoriesByRank)
  })

  return { mainCategories, subcategoriesByParent }
}

/**
 * Normalizes a category handle for consistent matching.
 * - Trims leading/trailing whitespace.
 * - Removes leading/trailing dashes.
 */
export const normalizeHandle = (handle?: string | null): string => {
  if (!handle) {
    return ""
  }
  return handle.trim().replace(/^-+|-+$/g, "")
}

type CategoryRouteSource = Pick<
  HttpTypes.StoreProductCategory,
  "id" | "handle" | "parent_category_id"
>

/**
 * The slice of a category the header menus need. The menus are client
 * components, so whatever they receive is serialized into the HTML of every
 * page — pass this instead of the full category (which drags along HTML
 * descriptions, metadata and nested parent/children copies).
 */
export type NavCategory = Pick<
  HttpTypes.StoreProductCategory,
  "id" | "name" | "handle" | "parent_category_id" | "rank"
>

export const toNavCategory = (
  category: HttpTypes.StoreProductCategory
): NavCategory => ({
  id: category.id,
  name: category.name,
  handle: category.handle,
  parent_category_id: category.parent_category_id ?? null,
  rank: category.rank,
})

export const getCategoryRoutePath = (
  category: CategoryRouteSource,
  allCategories: CategoryRouteSource[]
): string => {
  const routeHandles: string[] = []
  const visitedCategoryIds = new Set<string>()
  let currentCategory: CategoryRouteSource | undefined = category

  while (currentCategory?.handle && !visitedCategoryIds.has(currentCategory.id)) {
    visitedCategoryIds.add(currentCategory.id)
    routeHandles.unshift(currentCategory.handle)

    if (!currentCategory.parent_category_id) {
      break
    }

    currentCategory = allCategories.find(
      (item) => item.id === currentCategory?.parent_category_id
    )
  }

  return routeHandles.join("/")
}

export const findCategoryByRoutePath = <T extends CategoryRouteSource>(
  categoryPath: string,
  allCategories: T[]
): T | undefined => {
  const normalizedCategoryPath = normalizeHandle(categoryPath)

  return allCategories.find((category) => {
    const normalizedHandle = normalizeHandle(category.handle)
    const normalizedRoutePath = normalizeHandle(
      getCategoryRoutePath(category, allCategories)
    )

    return (
      normalizedHandle === normalizedCategoryPath ||
      normalizedRoutePath === normalizedCategoryPath
    )
  })
}

/**
 * Resolve Strapi category content for homepage tiles and other visual surfaces.
 *
 * Prefer an exact handle match first (e.g. Medusa `bar` → Strapi `bar` with the
 * square collage banner), then fall back to `systemId`. Some legacy Strapi rows
 * use a `-trgovina` handle but still carry the Medusa id — handle-first avoids
 * picking a portrait hero that crops to blank in wide, short cards.
 */
export const findStrapiCategoryForDisplay = <
  T extends {
    systemId?: string | null
    handle?: string | null
    slug?: string | null
  }
>(
  currentCategory: Pick<HttpTypes.StoreProductCategory, "id" | "handle">,
  strapiCategories: T[]
): T | null => {
  if (!Array.isArray(strapiCategories) || strapiCategories.length === 0) {
    return null
  }

  const normHandle = normalizeHandle(currentCategory.handle)
  if (normHandle) {
    const byHandle = strapiCategories.find(
      (c) =>
        normalizeHandle(c.handle) === normHandle ||
        normalizeHandle(c.slug) === normHandle
    )
    if (byHandle) {
      return byHandle
    }
  }

  const bySystemId = strapiCategories.find(
    (c) => c.systemId && c.systemId === currentCategory.id
  )
  return bySystemId ?? null
}

/**
 * Link a Medusa category to its Strapi content entry.
 *
 * Primary key is the Medusa category id, stored on the Strapi entry as
 * `systemId` (e.g. `pcat_01KS7W0Q1GDTAM3SHJ50CBFCAE`). This is stable even when
 * handles diverge or change. Handle / routePath matching is kept only as a
 * fallback for entries whose `systemId` has not been backfilled yet.
 */
export const findStrapiCategoryBySystemId = <
  T extends {
    systemId?: string | null
    handle?: string | null
    slug?: string | null
  }
>(
  currentCategory: Pick<HttpTypes.StoreProductCategory, "id" | "handle">,
  routePath: string,
  strapiCategories: T[]
): T | null => {
  if (!Array.isArray(strapiCategories) || strapiCategories.length === 0) {
    return null
  }

  // 1) Authoritative: Medusa id === Strapi systemId
  const bySystemId = strapiCategories.find(
    (c) => c.systemId && c.systemId === currentCategory.id
  )
  if (bySystemId) {
    return bySystemId
  }

  // 2) Fallback: handle / routePath (for not-yet-backfilled entries)
  const normRoute = normalizeHandle(routePath)
  const normHandle = normalizeHandle(currentCategory.handle)
  return (
    strapiCategories.find(
      (c) =>
        normalizeHandle(c.handle) === normRoute ||
        normalizeHandle(c.slug) === normRoute ||
        normalizeHandle(c.handle) === normHandle ||
        normalizeHandle(c.slug) === normHandle
    ) || null
  )
}

/**
 * The segment(s) after `/categories/` in a storefront pathname (no leading slash),
 * e.g. `leaf` or `parent/sub/leaf`. Empty if not on a category URL.
 */
export const getStorefrontCategoryPathTail = (pathname: string): string => {
  const pathNoQuery = pathname.split("?")[0]?.replace(/\/$/, "") ?? ""
  const marker = "/categories/"
  const idx = pathNoQuery.indexOf(marker)
  if (idx === -1) {
    return ""
  }
  let tail = pathNoQuery.slice(idx + marker.length)
  try {
    tail = decodeURIComponent(tail)
  } catch {
    // keep encoded tail
  }
  return tail.trim()
}

/**
 * True when this category is the one resolved from the URL, or an ancestor of it.
 * Supports both flat `/categories/{handle}` and nested `/categories/parent/.../handle`
 * (see {@link getCategoryRoutePath}).
 */
export const isCategoryActiveInPathname = (
  pathname: string,
  category: CategoryRouteSource,
  allCategories: CategoryRouteSource[]
): boolean => {
  const tail = getStorefrontCategoryPathTail(pathname)
  if (!tail) {
    return false
  }
  const current = findCategoryByRoutePath(tail, allCategories)
  if (!current) {
    return false
  }
  if (current.id === category.id) {
    return true
  }
  let walk: CategoryRouteSource | undefined = current
  while (walk?.parent_category_id) {
    const parent = allCategories.find((c) => c.id === walk!.parent_category_id)
    if (!parent) {
      break
    }
    if (parent.id === category.id) {
      return true
    }
    walk = parent
  }
  return false
}